/**
 * La herramienta del paso actual.
 *
 * Solo se muestra la herramienta que ESTE paso, en ESTA duración, realmente
 * usa, y siempre con la explicación de qué hacer con ella. Nunca se abre un
 * sitio sin decir qué buscar.
 */

import { useState } from 'react';

import { AVISO_FUENTES_AGOTADAS } from '../../domain/reglas/checkpoints';
import { AVISO_SIN_CONEXION, useEstaEnLinea } from '../comunes/useEstaEnLinea';
import { FUENTE_LISTENING_POR_CLAVE } from '../../curriculum/v1/fuentesListening';
import { HERRAMIENTA_POR_CLAVE } from '../../curriculum/v1/herramientas';
import type { Paso } from '../../curriculum/tipos';
import { compartirTexto, copiarTexto, puedeCompartir } from '../comunes/portapapeles';
import estilos from './HerramientaDelPaso.module.css';

interface Props {
  paso: Paso;
  /** Prompt ya compuesto (base + énfasis + foco + targets). */
  prompt: string | null;
  /** Se llama ANTES de abrir cualquier enlace externo, para persistir. */
  onAntesDeSalir: () => Promise<void>;
  /** Target activo escrito por el usuario, para YouGlish / Accent Coach. */
  target: string;
  onCambiarTarget: (valor: string) => void;
  /**
   * Fuente de listening asignada a esta medición, si la hay.
   *
   * La decide el dominio al crear la sesión, no este componente: la regla de
   * «no repetir material ya escuchado» no puede vivir en la interfaz.
   */
  fuenteListeningClave?: string;
}

/** URL de búsqueda de YouGlish. Es de terceros y puede cambiar: hay fallback. */
function urlYouglish(target: string): string {
  return `https://youglish.com/pronounce/${encodeURIComponent(target)}/english/us`;
}

export function HerramientaDelPaso({
  paso,
  prompt,
  onAntesDeSalir,
  target,
  onCambiarTarget,
  fuenteListeningClave,
}: Props) {
  const [copia, setCopia] = useState<'inicial' | 'copiado' | 'fallo'>('inicial');
  const [copiaTarget, setCopiaTarget] = useState<'inicial' | 'copiado' | 'fallo'>('inicial');
  const enLinea = useEstaEnLinea();

  if (paso.herramienta === 'ninguna') return null;

  const uso = paso.uso;
  const fuenteAsignada = fuenteListeningClave
    ? (FUENTE_LISTENING_POR_CLAVE[fuenteListeningClave] ?? null)
    : null;
  const ficha =
    paso.herramienta !== 'segunFoco'
      ? HERRAMIENTA_POR_CLAVE[paso.herramienta]
      : undefined;

  /**
   * Sin conexión, la herramienta externa no va a abrir. Se AVISA y no se
   * bloquea: `navigator.onLine` solo sabe si hay interfaz de red, no si
   * internet responde, y deshabilitar el botón por una señal aproximada
   * sería peor que dejar intentarlo.
   */
  const avisoDeRed =
    !enLinea && paso.herramienta !== 'grabadora' && paso.herramienta !== 'humano' ? (
      <p className={estilos.fallback} role="status">
        {AVISO_SIN_CONEXION}
      </p>
    ) : null;

  async function abrir(url: string) {
    // Persistir SIEMPRE antes de salir: puede que no volvamos a este proceso.
    await onAntesDeSalir();
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  async function copiarPrompt() {
    if (!prompt) return;
    const resultado = await copiarTexto(prompt);
    setCopia(resultado === 'copiado' ? 'copiado' : 'fallo');
  }

  async function copiarElTarget() {
    const resultado = await copiarTexto(target);
    setCopiaTarget(resultado === 'copiado' ? 'copiado' : 'fallo');
  }

  return (
    <section className={estilos.bloque} aria-label="Herramienta de este paso">
      {ficha ? (
        <h3 className={estilos.titulo}>
          <span aria-hidden="true">{ficha.icono}</span> {ficha.nombre}
        </h3>
      ) : (
        <h3 className={estilos.titulo}>Según tu foco de hoy</h3>
      )}

      {uso?.queHacer ? <p className={estilos.queHacer}>{uso.queHacer}</p> : null}

      {avisoDeRed}

      {/* ── ChatGPT Voice ────────────────────────────────────────────── */}
      {paso.herramienta === 'chatgpt' && prompt ? (
        <div className={estilos.chatgpt}>
          <label className={estilos.etiqueta} htmlFor="prompt-paso">
            Prompt de hoy
          </label>
          <textarea
            id="prompt-paso"
            className={estilos.prompt}
            value={prompt}
            readOnly
            rows={6}
          />

          <button type="button" className={estilos.principal} onClick={() => void copiarPrompt()}>
            Copiar prompt
          </button>

          {copia === 'copiado' ? (
            <p className={estilos.exito} role="status">
              ✓ Prompt copiado
            </p>
          ) : null}
          {copia === 'fallo' ? (
            <p className={estilos.fallo} role="alert">
              No se pudo copiar automáticamente. Tocá el texto de arriba,
              seleccionálo entero y copiálo a mano.
            </p>
          ) : null}

          <p className={estilos.aclaracion}>
            Se abrirá ChatGPT. Pegá el prompt y entrá a Voice.
          </p>

          <div className={estilos.acciones}>
            <button
              type="button"
              className={estilos.secundario}
              onClick={() => void abrir('https://chatgpt.com/')}
            >
              Abrir ChatGPT
            </button>
            {puedeCompartir() ? (
              <button
                type="button"
                className={estilos.secundario}
                onClick={() => void compartirTexto(prompt)}
              >
                Compartir
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* ── YouGlish ─────────────────────────────────────────────────── */}
      {paso.herramienta === 'youglish' ? (
        <div className={estilos.youglish}>
          <label className={estilos.etiqueta} htmlFor="target-youglish">
            ¿Qué estás buscando hoy?
          </label>
          <input
            id="target-youglish"
            className={estilos.campo}
            value={target}
            onChange={(evento) => onCambiarTarget(evento.target.value)}
            placeholder="una palabra o frase exacta"
          />

          {uso?.ejemplosBusqueda?.length ? (
            <div className={estilos.ejemplos}>
              <span className={estilos.etiqueta}>Ejemplos</span>
              <div className={estilos.chips}>
                {uso.ejemplosBusqueda.map((ejemplo) => (
                  <button
                    key={ejemplo}
                    type="button"
                    className={estilos.chip}
                    onClick={() => onCambiarTarget(ejemplo)}
                  >
                    {ejemplo}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {uso?.requiereTarget && uso.fallbackSinTarget && !target.trim() ? (
            <p className={estilos.fallback}>{uso.fallbackSinTarget}</p>
          ) : null}

          <div className={estilos.acciones}>
            <button
              type="button"
              className={estilos.secundario}
              onClick={() => void copiarElTarget()}
              disabled={!target.trim()}
            >
              Copiar target
            </button>
            <button
              type="button"
              className={estilos.principal}
              onClick={() => void abrir(target.trim() ? urlYouglish(target.trim()) : 'https://youglish.com/')}
            >
              Abrir YouGlish
            </button>
          </div>
          {copiaTarget === 'copiado' ? (
            <p className={estilos.exito} role="status">
              ✓ Target copiado
            </p>
          ) : null}
          <p className={estilos.aclaracion}>
            Si la búsqueda no se abre sola, pegá el target en el buscador de YouGlish.
          </p>
        </div>
      ) : null}

      {/* ── English Accent Coach ─────────────────────────────────────── */}
      {paso.herramienta === 'accentCoach' ? (
        <div>
          <label className={estilos.etiqueta} htmlFor="target-accent">
            Tu objetivo de hoy
          </label>
          <input
            id="target-accent"
            className={estilos.campo}
            value={target}
            onChange={(evento) => onCambiarTarget(evento.target.value)}
            placeholder="el contraste o patrón que vas a trabajar"
          />

          {!target.trim() && uso?.fallbackSinTarget ? (
            <p className={estilos.fallback}>{uso.fallbackSinTarget}</p>
          ) : null}

          <div className={estilos.acciones}>
            <button
              type="button"
              className={estilos.principal}
              onClick={() => void abrir('https://www.englishaccentcoach.com/')}
            >
              Abrir Accent Coach
            </button>
          </div>
        </div>
      ) : null}

      {/* ── Muestra auténtica de listening ───────────────────────────── */}
      {paso.herramienta === 'muestraListening' ? (
        <div>
          {/* La fuente asignada va PRIMERO y destacada: es la que garantiza
              que esta medición no repita material ya escuchado. Las demás
              siguen ahí porque ninguna URL puede ser un requisito. */}
          {fuenteAsignada ? (
            <div className={estilos.asignada}>
              <p className={estilos.etiqueta}>Te toca esta, que no usaste antes</p>
              <button
                type="button"
                className={estilos.enlaceFuente}
                onClick={() => void abrir(fuenteAsignada.url)}
              >
                {fuenteAsignada.nombre}
              </button>
              <p className={estilos.aclaracion}>{fuenteAsignada.porQueSirve}</p>
            </div>
          ) : (
            <p className={estilos.fallback}>{AVISO_FUENTES_AGOTADAS}</p>
          )}

          <p className={estilos.etiqueta}>
            {fuenteAsignada ? 'Otras fuentes, por si esa no te sirve hoy' : 'Fuentes sugeridas'}
          </p>
          <ul className={estilos.fuentes}>
            {(uso?.fuentesSugeridas ?? []).map((clave) => {
              const fuente = FUENTE_LISTENING_POR_CLAVE[clave];
              if (!fuente || clave === fuenteListeningClave) return null;
              return (
                <li key={clave}>
                  <button
                    type="button"
                    className={estilos.enlaceFuente}
                    onClick={() => void abrir(fuente.url)}
                  >
                    {fuente.nombre}
                  </button>
                  <span className={estilos.transcript}>
                    {fuente.transcript === 'si'
                      ? 'con transcripción'
                      : fuente.transcript === 'a-veces'
                        ? 'a veces con transcripción'
                        : 'sin transcripción'}
                  </span>
                </li>
              );
            })}
          </ul>
          {uso?.fallbackSinFuente ? (
            <p className={estilos.fallback}>{uso.fallbackSinFuente}</p>
          ) : null}
        </div>
      ) : null}

      {/* ── Persona real ─────────────────────────────────────────────── */}
      {paso.herramienta === 'humano' ? (
        <div>
          <p className={estilos.aclaracion}>No necesitás ninguna aplicación.</p>
          {prompt ? (
            <>
              <p className={estilos.etiqueta}>Si hoy no conseguís una persona real</p>
              <textarea className={estilos.prompt} value={prompt} readOnly rows={4} />
              <button
                type="button"
                className={estilos.secundario}
                onClick={() => void copiarPrompt()}
              >
                Copiar prompt de respaldo
              </button>
              {copia === 'copiado' ? (
                <p className={estilos.exito} role="status">
                  ✓ Prompt copiado
                </p>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      {paso.advertencia ? (
        <p className={estilos.advertencia} role="note">
          {paso.advertencia}
        </p>
      ) : null}
    </section>
  );
}
